import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Search, Lock, Crown, Flame, Beef, Heart, ShoppingCart, CheckSquare, Square,
  TrendingUp, CalendarRange, NotebookPen, Share2, Trophy, Loader2, Download,
  SlidersHorizontal, X, Fish, Leaf, WheatOff, Sun, Snowflake, Candy, UtensilsCrossed, Timer, RotateCcw,
  MilkOff, Ban, Star,
} from 'lucide-react';

const FILTER_GROUPS = (en) => ([
  { key: 'taste', label: en ? 'Taste' : 'Goût', options: [
    { v: 'sale', l: en ? 'Salty' : 'Salé', Icon: UtensilsCrossed },
    { v: 'sucre', l: en ? 'Sweet' : 'Sucré', Icon: Candy },
  ] },
  { key: 'temperature', label: en ? 'Temperature' : 'Température', options: [
    { v: 'chaud', l: en ? 'Hot' : 'Chaud', Icon: Sun },
    { v: 'froid', l: en ? 'Cold' : 'Froid', Icon: Snowflake },
  ] },
  { key: 'diet', label: en ? 'Diet' : 'Régime', options: [
    { v: 'viande', l: en ? 'Meat' : 'Viande', Icon: Beef },
    { v: 'poisson', l: en ? 'Fish' : 'Poisson', Icon: Fish },
    { v: 'vegetarien', l: en ? 'Vegetarian' : 'Végétarien', Icon: Leaf },
  ] },
  { key: 'restriction', label: en ? 'Restrictions (combinable)' : 'Restrictions (cumulables)', toggle: true, options: [
    { v: 'sans_gluten', l: en ? 'Gluten-free' : 'Sans gluten', Icon: WheatOff, stateKey: 'glutenFree' },
    { v: 'sans_porc', l: en ? 'Pork-free' : 'Sans porc', Icon: Ban, stateKey: 'porkFree' },
    { v: 'sans_lactose', l: en ? 'Lactose-free' : 'Sans lactose', Icon: MilkOff, stateKey: 'lactoseFree' },
    { v: 'sans_caseine', l: en ? 'Casein-free' : 'Sans caséine', Icon: MilkOff, stateKey: 'caseineFree' },
  ] },
  { key: 'calMax', label: en ? 'Calories' : 'Calories', options: [
    { v: 250, l: '< 250 kcal', Icon: Flame },
    { v: 500, l: '< 500 kcal', Icon: Flame },
  ] },
  { key: 'macro', label: en ? 'Macros' : 'Macros', toggle: true, options: [
    { v: 'low_carb', l: en ? 'Low carb' : 'Low carb', Icon: Leaf, stateKey: 'lowCarb' },
    { v: 'low_fat', l: en ? 'Low fat' : 'Low fat', Icon: Leaf, stateKey: 'lowFat' },
    { v: 'high_protein', l: en ? 'High protein' : 'Riche en protéines', Icon: Beef, stateKey: 'highProtein' },
  ] },
  { key: 'prepMax', label: en ? 'Prep time' : 'Temps de prépa', options: [
    { v: 5, l: en ? '< 5 min' : '< 5 min', Icon: Timer },
    { v: 10, l: en ? '< 10 min' : '< 10 min', Icon: Timer },
  ] },
]);
import { RecipeDetail } from './RecipeDetailModal';
import { ShoppingListModal, TopOfMonthModal } from './RecipeExtraModals';

const CategoryTabs = ({ categories, active, onPick, en, totalCount }) => (
  <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="recipe-category-tabs">
    <button
      onClick={() => onPick('')}
      className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold border transition-colors ${!active ? 'bg-primary border-primary text-white shadow-[0_0_20px_-4px_rgba(217,70,239,0.7)]' : 'border-white/10 text-zinc-400 hover:border-primary/40 hover:text-white'}`}
      data-testid="recipe-tab-all"
    >
      {en ? 'All' : 'Toutes'} ({totalCount})
    </button>
    {categories.map((c) => (
      <button
        key={c.key}
        onClick={() => onPick(c.key)}
        className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold border transition-colors ${active === c.key ? 'bg-primary border-primary text-white shadow-[0_0_20px_-4px_rgba(217,70,239,0.7)]' : 'border-white/10 text-zinc-400 hover:border-primary/40 hover:text-white'}`}
        data-testid={`recipe-tab-${c.key}`}
      >
        {en ? c.label_en : c.label_fr} ({c.count})
      </button>
    ))}
  </div>
);

const QuickFilterChip = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${active ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-300' : 'border-white/10 text-zinc-400 hover:border-yellow-500/40 hover:text-yellow-200'}`}
    data-testid={testId}
  >
    {children}
  </button>
);

/* ---------- Thème de la semaine (mise en avant de 5-7 recettes) ---------- */
const ThemeStrip = ({ theme, recipes, en, onOpen, onDownloadPdf, downloading }) => {
  if (!theme) return null;
  const themeRecipes = theme.recipe_ids.map((id) => recipes.find((r) => r.id === id)).filter(Boolean);
  if (themeRecipes.length === 0) return null;
  return (
    <div className="mb-5" data-testid="theme-of-week-strip">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-xs font-bold uppercase tracking-wide text-yellow-300 flex items-center gap-1.5">
          <CalendarRange className="w-3.5 h-3.5" /> {en ? 'Theme of the week' : 'Thème de la semaine'} : {en ? theme.name_en : theme.name_fr}
        </p>
        <button
          onClick={onDownloadPdf} disabled={downloading}
          className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border border-white/10 text-zinc-400 hover:border-yellow-500/40 hover:text-yellow-200 transition-colors disabled:opacity-50"
          data-testid="download-theme-pdf-btn"
        >
          {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} PDF
        </button>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
        {themeRecipes.map((r) => (
          <button
            key={r.id} onClick={() => onOpen(r)}
            className="shrink-0 w-28 text-left"
            data-testid={`theme-card-${r.id}`}
          >
            <div className="w-28 h-28 rounded-xl overflow-hidden border border-white/10">
              <img src={r.image_url} alt="" loading="lazy" className="w-full h-full object-cover" />
            </div>
            <p className="text-xs text-white font-semibold mt-1.5 line-clamp-2">{en ? r.name_en : r.name_fr}</p>
          </button>
        ))}
      </div>
    </div>
  );
};

const TASTE_ICON = { sale: UtensilsCrossed, sucre: Candy };
const TEMP_ICON = { chaud: Sun, froid: Snowflake };
const DIET_ICON = { viande: Beef, poisson: Fish, vegetarien: Leaf };

const TagIcon = ({ Icon, title }) => (
  <div className="w-5 h-5 rounded-full bg-black/70 backdrop-blur-sm flex items-center justify-center shrink-0" title={title}>
    <Icon className="w-3 h-3 text-zinc-200" />
  </div>
);

const RecipeCard = ({ recipe, en, onOpen, selectionMode, isSelected, onToggleSelect, isFavorite, onToggleFavorite }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
    role="button" tabIndex={0}
    onClick={() => (selectionMode ? onToggleSelect(recipe.id) : onOpen(recipe))}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (selectionMode ? onToggleSelect(recipe.id) : onOpen(recipe)); } }}
    className={`group relative rounded-2xl overflow-hidden border bg-zinc-900 aspect-[4/5] text-left shadow-[0_10px_30px_rgba(0,0,0,0.5)] transition-colors cursor-pointer ${selectionMode && isSelected ? 'border-primary ring-2 ring-primary/50' : 'border-white/10 hover:border-primary/50'}`}
    data-testid={`recipe-card-${recipe.id}`}
  >
    <img src={recipe.image_url} alt={en ? recipe.name_en : recipe.name_fr} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/45 to-black/10" />

    {recipe.is_popular && (
      <div className="absolute top-2.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-yellow-500/90 text-black" data-testid={`recipe-popular-badge-${recipe.id}`}>
        <TrendingUp className="w-2.5 h-2.5" /> {en ? 'Popular' : 'Populaire'}
      </div>
    )}

    <button
      onClick={(e) => { e.stopPropagation(); onToggleFavorite(recipe.id); }}
      className="absolute top-2.5 left-2.5 w-8 h-8 rounded-full bg-black/70 backdrop-blur-sm flex items-center justify-center hover:bg-black/90 transition-colors"
      data-testid={`recipe-favorite-btn-${recipe.id}`}
    >
      <Heart className={`w-4 h-4 ${isFavorite ? 'text-primary fill-primary' : 'text-white'}`} />
    </button>

    {(recipe.my_note || recipe.is_shared) && (
      <div className="absolute top-12 left-2.5 flex flex-col gap-1.5">
        {recipe.my_note && (
          <div className="w-7 h-7 rounded-full bg-black/70 backdrop-blur-sm flex items-center justify-center" data-testid={`recipe-note-indicator-${recipe.id}`} title={en ? 'Has a personal note' : 'A une note personnelle'}>
            <NotebookPen className="w-3.5 h-3.5 text-yellow-300" />
          </div>
        )}
        {recipe.is_shared && (
          <div className="w-7 h-7 rounded-full bg-black/70 backdrop-blur-sm flex items-center justify-center" data-testid={`recipe-shared-indicator-${recipe.id}`} title={en ? 'Already shared' : 'Déjà partagée'}>
            <Share2 className="w-3.5 h-3.5 text-emerald-300" />
          </div>
        )}
      </div>
    )}

    {selectionMode ? (
      <div className="absolute top-2.5 right-2.5" data-testid={`recipe-select-check-${recipe.id}`}>
        {isSelected ? <CheckSquare className="w-6 h-6 text-primary bg-black/70 rounded" /> : <Square className="w-6 h-6 text-white bg-black/70 rounded" />}
      </div>
    ) : (
      <div className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-black/75 backdrop-blur-sm text-primary border border-primary/30">
        <Flame className="w-2.5 h-2.5" /> {Math.round(recipe.macros.calories)} kcal
      </div>
    )}

    <div className="absolute bottom-0 left-0 right-0 p-3">
      {(recipe.temperature || recipe.taste || recipe.diet?.length || recipe.gluten_free) && (
        <div className="flex items-center gap-1 mb-1.5" data-testid={`recipe-tags-${recipe.id}`}>
          {recipe.temperature && <TagIcon Icon={TEMP_ICON[recipe.temperature]} title={recipe.temperature === 'chaud' ? (en ? 'Hot' : 'Chaud') : (en ? 'Cold' : 'Froid')} />}
          {recipe.taste && <TagIcon Icon={TASTE_ICON[recipe.taste]} title={recipe.taste === 'sale' ? (en ? 'Salty' : 'Salé') : (en ? 'Sweet' : 'Sucré')} />}
          {recipe.diet?.[0] && <TagIcon Icon={DIET_ICON[recipe.diet[0]]} title={recipe.diet[0]} />}
          {recipe.gluten_free && <TagIcon Icon={WheatOff} title={en ? 'Gluten-free' : 'Sans gluten'} />}
        </div>
      )}
      <p className="text-white text-sm font-bold leading-snug line-clamp-2 drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]" style={{ fontFamily: 'Outfit, sans-serif' }}>{en ? recipe.name_en : recipe.name_fr}</p>
      <p className="text-[11px] text-zinc-200 mt-1 flex items-center gap-1.5 drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]"><Beef className="w-3 h-3 text-pink-400" /> {Math.round(recipe.macros.protein)}g {en ? 'protein' : 'protéines'}</p>
    </div>
  </motion.div>
);

/* ---------- Paywall ---------- */
const RecipesPaywall = ({ en, onUpgrade }) => {
  const [count, setCount] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recipes/count`).then((r) => r.json()).then((d) => setCount(d.count)).catch(() => {});
  }, []);
  const countLabel = count ? `+ de ${count}` : (en ? '150+' : '150+');
  return (
  <div className="rounded-3xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-8 sm:p-12 text-center" data-testid="recipes-paywall">
    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-400/20 to-primary/20 border border-yellow-500/30 flex items-center justify-center mx-auto mb-4">
      <Lock className="w-7 h-7 text-yellow-400" />
    </div>
    <h3 className="text-xl font-black text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="recipes-count-label">{en ? `${countLabel} high-protein recipes` : `${countLabel} recettes protéinées`}</h3>
    <p className="text-zinc-400 max-w-md mx-auto mb-1">{en ? 'Starters, dishes, desserts & snacks with full macros, ready to log in one tap.' : 'Entrées, plats, desserts & collations avec macros complets, prêtes à logger en un clic.'}</p>
    <span className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 rounded-full text-sm font-bold bg-yellow-500/15 border border-yellow-500/40 text-yellow-300" data-testid="recipes-price-badge">
      <Crown className="w-4 h-4" /> {en ? 'Included from 9.90 CHF' : 'Inclus dès 9.90 CHF'}
    </span>
    <div>
      <button onClick={onUpgrade} className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-bold text-black bg-gradient-to-r from-yellow-400 to-primary" data-testid="recipes-upgrade-btn">
        <Crown className="w-4 h-4" /> {en ? 'Unlock with Suivi Pro' : 'Débloquer avec Suivi Pro'}
      </button>
    </div>
  </div>
  );
};

/* ---------- Vue principale ---------- */
export const RecipesLibrary = ({ api, en, onUpgrade, initialRecipeId, authFetch }) => {
  const [phase, setPhase] = useState('loading'); // loading | locked | ready | error
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [theme, setTheme] = useState(null);
  const [activeCategory, setActiveCategory] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [favOnly, setFavOnly] = useState(false);
  const [filters, setFilters] = useState({
    taste: null, temperature: null, diet: null, calMax: null, prepMax: null,
    lowCarb: false, lowFat: false, highProtein: false,
    glutenFree: false, porkFree: false, lactoseFree: false, caseineFree: false,
  });
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [shoppingListOpen, setShoppingListOpen] = useState(false);
  const [topOfMonthOpen, setTopOfMonthOpen] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [savingDefaultFilters, setSavingDefaultFilters] = useState(false);
  const [hasSavedDefaultFilters, setHasSavedDefaultFilters] = useState(false);

  useEffect(() => {
    authFetch('/api/recipes/favorite-filters').then((r) => r.json()).then((d) => {
      if (d.filters && Object.keys(d.filters).length > 0) {
        setFilters((f) => ({ ...f, ...d.filters }));
        setHasSavedDefaultFilters(true);
        setAdvancedOpen(true);
      }
    }).catch(() => {});
  }, [authFetch]);

  const saveDefaultFilters = async () => {
    setSavingDefaultFilters(true);
    try {
      await authFetch('/api/recipes/favorite-filters', { method: 'PUT', body: JSON.stringify({ filters }) });
      setHasSavedDefaultFilters(true);
      toast.success(en ? 'Default filters saved for next time' : 'Filtres enregistrés comme vue par défaut');
    } catch { toast.error(en ? 'Save failed' : 'Échec de la sauvegarde'); }
    setSavingDefaultFilters(false);
  };

  const clearDefaultFilters = async () => {
    setSavingDefaultFilters(true);
    try {
      await authFetch('/api/recipes/favorite-filters', { method: 'PUT', body: JSON.stringify({ filters: {} }) });
      setHasSavedDefaultFilters(false);
      toast.success(en ? 'Default filters cleared' : 'Filtres par défaut effacés');
    } catch { toast.error(en ? 'Failed' : 'Échec'); }
    setSavingDefaultFilters(false);
  };

  useEffect(() => {
    let active = true;
    api('/api/recipes')
      .then((d) => {
        if (!active) return;
        setRecipes(d.recipes || []);
        setCategories(d.categories || []);
        setPhase('ready');
      })
      .catch((r) => {
        if (!active) return;
        setPhase(r?.status === 403 ? 'locked' : 'error');
      });
    api('/api/recipes/theme-of-the-week').then((d) => { if (active) setTheme(d); }).catch(() => {});
    return () => { active = false; };
  }, [api]);

  const openedInitialRef = useRef(null);
  useEffect(() => {
    if (phase === 'ready' && initialRecipeId && openedInitialRef.current !== initialRecipeId) {
      const r = recipes.find((x) => x.id === initialRecipeId);
      if (r) { setSelected(r); openedInitialRef.current = initialRecipeId; }
    }
  }, [phase, initialRecipeId, recipes]);

  const toggleFavorite = (recipeId) => {
    const recipe = recipes.find((r) => r.id === recipeId);
    if (!recipe) return;
    const next = !recipe.is_favorite;
    setRecipes((prev) => prev.map((r) => (r.id === recipeId ? { ...r, is_favorite: next } : r)));
    setSelected((prev) => (prev && prev.id === recipeId ? { ...prev, is_favorite: next } : prev));
    api(`/api/recipes/${recipeId}/favorite`, { method: next ? 'POST' : 'DELETE' }).catch(() => {
      setRecipes((prev) => prev.map((r) => (r.id === recipeId ? { ...r, is_favorite: !next } : r)));
      toast.error(en ? 'Could not update favorite' : "Impossible de mettre à jour le favori");
    });
  };

  const markShared = (recipeId) => {
    setRecipes((prev) => prev.map((r) => (r.id === recipeId ? { ...r, is_shared: true } : r)));
    setSelected((prev) => (prev && prev.id === recipeId ? { ...prev, is_shared: true } : prev));
  };

  const toggleSelect = (recipeId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(recipeId)) next.delete(recipeId); else next.add(recipeId);
      return next;
    });
  };

  const handleNoteSaved = (recipeId, note) => {
    setRecipes((prev) => prev.map((r) => (r.id === recipeId ? { ...r, my_note: note } : r)));
    setSelected((prev) => (prev && prev.id === recipeId ? { ...prev, my_note: note } : prev));
  };

  const refetchRecipes = () => {
    api('/api/recipes').then((d) => { setRecipes(d.recipes || []); setCategories(d.categories || []); }).catch(() => {});
  };

  const openFromRanking = (recipe) => {
    setTopOfMonthOpen(false);
    setSelected(recipe);
  };

  const downloadThemePdf = async () => {
    if (!theme) return;
    setDownloadingPdf(true);
    try {
      const res = await authFetch(`/api/recipes/theme-of-the-week/pdf${en ? '?lang=en' : ''}`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `theme-semaine-${theme.key}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error(en ? 'Could not download the PDF' : 'Impossible de télécharger le PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const filtered = useMemo(() => {
    let list = activeCategory ? recipes.filter((r) => r.category === activeCategory) : recipes;
    if (favOnly) list = list.filter((r) => r.is_favorite);
    if (filters.taste) list = list.filter((r) => r.taste === filters.taste);
    if (filters.temperature) list = list.filter((r) => r.temperature === filters.temperature);
    if (filters.diet) list = list.filter((r) => (r.diet || []).includes(filters.diet));
    if (filters.calMax) list = list.filter((r) => r.macros.calories < filters.calMax);
    if (filters.prepMax) list = list.filter((r) => r.prep_min < filters.prepMax);
    if (filters.lowCarb) list = list.filter((r) => r.macros.carbs <= 20);
    if (filters.lowFat) list = list.filter((r) => r.macros.fat <= 10);
    if (filters.highProtein) list = list.filter((r) => r.macros.protein >= 30);
    if (filters.glutenFree) list = list.filter((r) => r.gluten_free);
    if (filters.porkFree) list = list.filter((r) => r.pork_free);
    if (filters.lactoseFree) list = list.filter((r) => r.dairy_free);
    if (filters.caseineFree) list = list.filter((r) => r.dairy_free);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((r) => (en ? r.name_en : r.name_fr).toLowerCase().includes(q) || (r.my_note || '').toLowerCase().includes(q));
    return list;
  }, [recipes, activeCategory, favOnly, filters, query, en]);

  const activeFilterCount = Object.entries(filters).filter(([, v]) => v !== null && v !== false).length;
  const resetFilters = () => setFilters({
    taste: null, temperature: null, diet: null, calMax: null, prepMax: null,
    lowCarb: false, lowFat: false, highProtein: false,
    glutenFree: false, porkFree: false, lactoseFree: false, caseineFree: false,
  });
  const toggleRadioFilter = (key, value) => setFilters((f) => ({ ...f, [key]: f[key] === value ? null : value }));
  const toggleBoolFilter = (key) => setFilters((f) => ({ ...f, [key]: !f[key] }));

  if (phase === 'loading') {
    return <div className="flex items-center justify-center py-24" data-testid="recipes-loading"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;
  }
  if (phase === 'locked') {
    return <RecipesPaywall en={en} onUpgrade={onUpgrade} />;
  }
  if (phase === 'error') {
    return <p className="text-red-400 text-sm text-center py-12" data-testid="recipes-error">{en ? 'Could not load recipes.' : 'Impossible de charger les recettes.'}</p>;
  }

  const favCount = recipes.filter((r) => r.is_favorite).length;
  const selectedRecipes = recipes.filter((r) => selectedIds.has(r.id));

  return (
    <div data-testid="recipes-library-view">
      <div className="relative mb-4">
        <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder={en ? 'Search a recipe or a note…' : 'Rechercher une recette ou une note…'}
          className="w-full pl-9 pr-3 py-2.5 rounded-full text-sm bg-white/[0.035] backdrop-blur-xl border border-white/10 text-white placeholder:text-zinc-500 focus:outline-none focus:border-primary/50"
          data-testid="recipes-search-input"
        />
      </div>

      <CategoryTabs categories={categories} active={activeCategory} onPick={setActiveCategory} en={en} totalCount={recipes.length} />

      <ThemeStrip theme={theme} recipes={recipes} en={en} onOpen={setSelected} onDownloadPdf={downloadThemePdf} downloading={downloadingPdf} />

      <div className="flex flex-wrap items-center gap-2 mt-3">
        <QuickFilterChip active={favOnly} onClick={() => setFavOnly((v) => !v)} testId="filter-favorites-only">
          <Heart className={`w-3 h-3 inline mr-1 -mt-0.5 ${favOnly ? 'fill-yellow-300' : ''}`} /> {en ? 'My favorites' : 'Mes favoris'} ({favCount})
        </QuickFilterChip>
        <button
          onClick={() => setAdvancedOpen((v) => !v)}
          className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${advancedOpen || activeFilterCount > 0 ? 'bg-primary/20 border-primary text-white' : 'border-white/10 text-zinc-400 hover:border-primary/40 hover:text-white'}`}
          data-testid="toggle-advanced-filters-btn"
        >
          <SlidersHorizontal className="w-3 h-3" /> {en ? 'Advanced filters' : 'Filtres avancés'}
          {activeFilterCount > 0 && <span className="ml-0.5 px-1.5 rounded-full bg-primary text-white text-[10px]" data-testid="advanced-filters-count">{activeFilterCount}</span>}
        </button>
        <button
          onClick={() => setTopOfMonthOpen(true)}
          className="shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold border border-white/10 text-zinc-400 hover:border-yellow-500/40 hover:text-yellow-200 transition-colors"
          data-testid="open-top-of-month-btn"
        >
          <Trophy className="w-3 h-3 inline mr-1 -mt-0.5" /> {en ? 'Top recipes' : 'Top recettes'}
        </button>
        <button
          onClick={() => { setSelectionMode((v) => !v); setSelectedIds(new Set()); }}
          className={`ml-auto shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${selectionMode ? 'bg-primary/20 border-primary text-white' : 'border-white/10 text-zinc-400 hover:border-primary/40 hover:text-white'}`}
          data-testid="toggle-selection-mode-btn"
        >
          <ShoppingCart className="w-3 h-3 inline mr-1 -mt-0.5" /> {selectionMode ? (en ? 'Cancel' : 'Annuler') : (en ? 'Shopping list' : 'Liste de courses')}
        </button>
      </div>

      <AnimatePresence>
        {advancedOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="mt-3 rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 p-4 overflow-hidden"
            data-testid="advanced-filters-panel"
          >
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-xs font-bold text-white uppercase tracking-wide">{en ? 'Pro filters' : 'Filtres pro'}</p>
              <div className="flex items-center gap-3">
                <button onClick={hasSavedDefaultFilters ? clearDefaultFilters : saveDefaultFilters} disabled={savingDefaultFilters}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:text-white disabled:opacity-50" data-testid="save-default-filters-btn">
                  {savingDefaultFilters ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className={`w-3 h-3 ${hasSavedDefaultFilters ? 'fill-primary' : ''}`} />}
                  {hasSavedDefaultFilters ? (en ? 'Forget default' : 'Oublier ce défaut') : (en ? 'Save as default' : 'Enregistrer par défaut')}
                </button>
                {activeFilterCount > 0 && (
                  <button onClick={resetFilters} className="inline-flex items-center gap-1 text-[11px] font-semibold text-zinc-400 hover:text-white" data-testid="reset-advanced-filters-btn">
                    <RotateCcw className="w-3 h-3" /> {en ? 'Reset' : 'Réinitialiser'}
                  </button>
                )}
                <button onClick={() => setAdvancedOpen(false)} className="text-zinc-500 hover:text-white" data-testid="close-advanced-filters-btn"><X className="w-4 h-4" /></button>
              </div>
            </div>
            {hasSavedDefaultFilters && (
              <p className="text-[11px] text-primary/80 -mt-1 mb-3 flex items-center gap-1" data-testid="default-filters-active-hint">
                <Star className="w-3 h-3 fill-primary" /> {en ? 'These filters open automatically every time.' : 'Ces filtres s\u2019ouvrent automatiquement à chaque visite.'}
              </p>
            )}
            <div className="space-y-3">
              {FILTER_GROUPS(en).map((group) => (
                <div key={group.key}>
                  <p className="text-[11px] text-zinc-500 mb-1.5">{group.label}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {group.options.map((opt) => {
                      const active = group.toggle ? !!filters[opt.stateKey] : filters[group.key] === opt.v;
                      const onClick = group.toggle ? () => toggleBoolFilter(opt.stateKey) : () => toggleRadioFilter(group.key, opt.v);
                      return (
                        <button
                          key={String(opt.v)} onClick={onClick}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold border transition-colors ${active ? 'bg-primary border-primary text-white' : 'border-white/10 text-zinc-400 hover:border-primary/40 hover:text-white'}`}
                          data-testid={`pro-filter-${group.key}-${opt.v}`}
                        >
                          <opt.Icon className="w-3 h-3" /> {opt.l}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {selectionMode && (
        <div className="sticky top-2 z-10 mt-3 flex items-center justify-between gap-3 rounded-2xl bg-primary/15 border border-primary/40 px-4 py-2.5" data-testid="selection-action-bar">
          <p className="text-sm text-white font-semibold">{en ? `${selectedIds.size} recipe(s) selected` : `${selectedIds.size} recette(s) sélectionnée(s)`}</p>
          <button
            onClick={() => setShoppingListOpen(true)}
            disabled={selectedIds.size === 0}
            className="px-4 py-1.5 rounded-full text-xs font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-40"
            data-testid="generate-shopping-list-btn"
          >
            {en ? 'Generate list' : 'Générer la liste'}
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="text-zinc-500 text-center py-16" data-testid="recipes-empty">{en ? 'No recipe found.' : 'Aucune recette trouvée.'}</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-5" data-testid="recipes-grid">
          {filtered.map((r) => (
            <RecipeCard
              key={r.id} recipe={r} en={en} onOpen={setSelected}
              selectionMode={selectionMode} isSelected={selectedIds.has(r.id)} onToggleSelect={toggleSelect}
              isFavorite={!!r.is_favorite} onToggleFavorite={toggleFavorite}
            />
          ))}
        </div>
      )}

      <AnimatePresence>
        {selected && (
          <RecipeDetail
            recipe={selected} en={en} api={api} authFetch={authFetch} onClose={() => setSelected(null)}
            isFavorite={!!selected.is_favorite} onToggleFavorite={toggleFavorite}
            onNoteSaved={handleNoteSaved} onRecipeLogged={refetchRecipes} onShared={markShared}
            allRecipes={recipes} onSwitchRecipe={setSelected}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {shoppingListOpen && <ShoppingListModal recipes={selectedRecipes} en={en} onClose={() => setShoppingListOpen(false)} />}
      </AnimatePresence>

      <AnimatePresence>
        {topOfMonthOpen && <TopOfMonthModal api={api} authFetch={authFetch} en={en} onClose={() => setTopOfMonthOpen(false)} onOpenRecipe={openFromRanking} recipesCount={recipes.length} />}
      </AnimatePresence>
    </div>
  );
};

export default RecipesLibrary;
